import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import { getKnowledgeHubStatus } from './api';
import KnowledgeHubDrawer from './KnowledgeHubDrawer';

type KnowledgeHubChatSide = 'left' | 'right';

const CHAT_SIDE_STORAGE_KEY = 'knowledgeHubChatSide';
const CHAT_SIDE_EVENT = 'knowledgeHubChatSideChanged';

function readChatSide(): KnowledgeHubChatSide {
  try {
    return window.localStorage.getItem(CHAT_SIDE_STORAGE_KEY) === 'left' ? 'left' : 'right';
  } catch {
    return 'right';
  }
}

interface KnowledgeHubLauncherProps {
  canAccess: boolean;
  showDebugScores?: boolean;
}

const KnowledgeHubLauncher: React.FC<KnowledgeHubLauncherProps> = ({ canAccess, showDebugScores = false }) => {
  const [open, setOpen] = useState(false);
  const [available, setAvailable] = useState(false);
  const [chatSide, setChatSide] = useState<KnowledgeHubChatSide>(() => readChatSide());

  const checkStatus = useCallback(async () => {
    if (!canAccess) {
      setAvailable(false);
      return;
    }

    try {
      const status = await getKnowledgeHubStatus();
      setAvailable(status.enabled === true);
    } catch {
      setAvailable(false);
    }
  }, [canAccess]);

  useEffect(() => {
    void checkStatus();
  }, [checkStatus]);

  useEffect(() => {
    const syncChatSide = () => setChatSide(readChatSide());
    window.addEventListener('storage', syncChatSide);
    window.addEventListener(CHAT_SIDE_EVENT, syncChatSide);
    return () => {
      window.removeEventListener('storage', syncChatSide);
      window.removeEventListener(CHAT_SIDE_EVENT, syncChatSide);
    };
  }, []);

  if (!canAccess || !available) {
    return null;
  }

  return (
    <>
      {!open && (
        <Box
          sx={{
            position: 'fixed',
            ...(chatSide === 'left' ? { left: 0 } : { right: 0 }),
            top: { xs: '44%', md: '50%' },
            transform: 'translateY(-50%)',
            zIndex: 1300,
          }}
        >
          <Tooltip title="Chatta med Athena" placement={chatSide === 'left' ? 'right' : 'left'}>
            <Box
              component="button"
              type="button"
              aria-label="Chatta med Athena"
              onClick={() => setOpen(true)}
              sx={{
                m: 0,
                p: 0,
                position: 'relative',
                width: { xs: 62, sm: 68 },
                height: { xs: 224, sm: 248 },
                overflow: 'hidden',
                border: '1px solid rgba(0, 0, 0, 0.4)',
                borderRadius: chatSide === 'left' ? '0 14px 14px 0' : '14px 0 0 14px',
                bgcolor: '#ffffff',
                color: '#1f2937',
                cursor: 'pointer',
                boxShadow: '0 12px 34px rgba(15, 23, 42, 0.22)',
                transition: 'box-shadow 160ms ease, background-color 160ms ease',
                '&:hover': {
                  bgcolor: '#f8fbff',
                  boxShadow: '0 16px 42px rgba(15, 23, 42, 0.3)',
                },
                '&:focus-visible': {
                  outline: '3px solid rgba(25, 118, 210, 0.35)',
                  outlineOffset: 3,
                },
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1.15,
                  transform: 'translate(-50%, -50%) rotate(-90deg)',
                  transformOrigin: 'center',
                  whiteSpace: 'nowrap',
                }}
              >
                <AutoAwesomeIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: '#0b5d91' }} />
                <Typography
                  component="span"
                  sx={{
                    fontSize: { xs: '0.82rem', sm: '0.9rem' },
                    fontWeight: 900,
                    letterSpacing: '0.08em',
                    lineHeight: 1,
                    textTransform: 'uppercase',
                  }}
                >
                  Chatta med Athena
                </Typography>
              </Box>
            </Box>
          </Tooltip>
        </Box>
      )}
      <KnowledgeHubDrawer
        open={open}
        onClose={() => setOpen(false)}
        showDebugScores={showDebugScores}
        anchor={chatSide}
      />
    </>
  );
};

export default KnowledgeHubLauncher;
